'use client';

import React, { useState } from 'react';
import { useSlidePanel } from '@/context/SlidePanelContext';
import { 
  Grid2X2, 
  Grid3X3, 
  List,
  CheckSquare,
  Activity,
  Edit,
  Calendar,
  Utensils,
  Wind,
  Camera,
  Stethoscope,
  TestTube,
  Pill,
  ShoppingBag,
  Watch,
  Video,
  Mic,
  Search,
  LucideIcon
} from 'lucide-react';

const iconComponents = {
  'TaskIcon': CheckSquare,
  'ActivityIcon': Activity,
  'EditIcon': Edit,
  'CalendarIcon': Calendar,
  'UtensilsIcon': Utensils,
  'BreatheIcon': Wind,
  'CameraIcon': Camera,
  'StethoscopeIcon': Stethoscope,
  'TestTubeIcon': TestTube,
  'PillIcon': Pill,
  'ShopIcon': ShoppingBag,
  'WatchIcon': Watch,
  'VideoIcon': Video,
  'MicrophoneIcon': Mic
} as const;

export function QuickActions() {
  const { 
    quickActions, 
    updateQuickActions, 
    viewLayout, 
    setViewLayout 
  } = useSlidePanel();

  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [draggedOverItem, setDraggedOverItem] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getLayoutClasses = () => {
    switch (viewLayout) {
      case 'grid-2':
        return 'grid grid-cols-2 gap-4';
      case 'grid-3':
        return 'grid grid-cols-3 gap-3';
      default:
        return 'flex flex-col gap-2';
    }
  };

  const handleDragStart = (id: string) => {
    setDraggedItem(id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDraggedOverItem(id);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const items = [...quickActions];
    const draggedIndex = items.findIndex(item => item.id === draggedItem);
    const targetIndex = items.findIndex(item => item.id === targetId);

    // Swap the items and update their order
    const updatedItems = items?.map(item => {
      if (item.id === draggedItem) {
        return { ...item, order: targetIndex };
      }
      if (item.id === targetId) {
        return { ...item, order: draggedIndex };
      }
      return item;
    });

    // Sort by order to maintain the sequence
    updatedItems.sort((a, b) => a.order - b.order);

    updateQuickActions(updatedItems);
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const renderIcon = (iconName: string) => {
    const IconComponent = iconComponents[iconName as keyof typeof iconComponents];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : null;
  };

  // Filter and sort actions based on search term
  const filteredAndSortedActions = [...quickActions]
    .filter(action => 
      action?.label?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.order - b.order);

  return (
    <div className="relative p-4">
      {/* View Layout Switcher */}
      <div className="absolute top-0 right-4 flex items-center gap-2 mb-4 p-2 bg-gray-800 rounded-lg">
        <button
          onClick={() => setViewLayout('list')}
          className={`p-1.5 rounded-md transition ${viewLayout === 'list' ? 'bg-gray-700' : 'hover:bg-gray-700/50'}`}
        >
          <List className="h-4 w-4" />
        </button>
        <button
          onClick={() => setViewLayout('grid-2')}
          className={`p-1.5 rounded-md transition ${viewLayout === 'grid-2' ? 'bg-gray-700' : 'hover:bg-gray-700/50'}`}
        >
          <Grid2X2 className="h-4 w-4" />
        </button>
        <button
          onClick={() => setViewLayout('grid-3')}
          className={`p-1.5 rounded-md transition ${viewLayout === 'grid-3' ? 'bg-gray-700' : 'hover:bg-gray-700/50'}`}
        >
          <Grid3X3 className="h-4 w-4" />
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative mt-12 mb-4">
        <input
          type="text"
          placeholder="Search actions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="w-full px-4 py-2 pl-10 bg-gray-800 text-white rounded-lg 
                   placeholder-gray-400 focus:outline-none focus:ring-2 
                   focus:ring-blue-500 transition-all"
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>

      {/* Quick Actions */}
      <div className={`${getLayoutClasses()}`}>
        {filteredAndSortedActions?.map((action) => (
          <div
            key={action.id}
            draggable
            onDragStart={() => handleDragStart(action.id)}
            onDragOver={(e) => handleDragOver(e, action.id)}
            onDrop={(e) => handleDrop(e, action.id)}
            className={`
              cursor-move bg-gray-800 rounded-lg p-4 
              hover:bg-gray-700 transition-colors
              flex items-center gap-3
              ${viewLayout === 'list' ? 'w-full' : 'w-auto'}
              ${viewLayout === 'grid-3' ? 'justify-center' : ''}
              ${draggedOverItem === action.id ? 'border-2 border-blue-500' : ''}
              ${draggedItem === action.id ? 'opacity-50' : ''}
            `}
          >
            {renderIcon(action.icon)}
            {viewLayout !== 'grid-3' && (
              <span className="font-medium">{action.label}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}